import type { ReactElement } from 'react';
import { Outlet } from 'react-router-dom';
import { Toaster } from 'sonner';
import { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

/**
 * Root route component.
 *
 * Acts as the single mount point for app-wide UI providers that must
 * live inside the router tree. Currently wires up:
 *
 * - `<SkeletonTheme>` (`react-loading-skeleton`) — propagates dark-theme
 *   colors to every `<Skeleton>` rendered downstream so feature code
 *   does not need to repeat the palette. The base/highlight values are
 *   the literal Tailwind tokens (`bg.card` / `bg.hover`) — kept inline
 *   because `react-loading-skeleton` reads CSS color strings, not class
 *   names, so a runtime token lookup would only add indirection.
 * - `<Toaster>` (sonner) — renders all toasts triggered through
 *   `@favelafood/shared/lib/toast` (or `sonner` directly) at the bottom-right with the
 *   dark theme + rich colors variant defined in DESIGN seção 11.1.
 *
 * Providers that don't need router context (`QueryClientProvider`) live
 * one level higher in `main.tsx`.
 */
export default function App(): ReactElement {
  return (
    <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
      <Outlet />
      <Toaster position="bottom-right" theme="dark" richColors closeButton />
    </SkeletonTheme>
  );
}
